/**
 * Per-surface Anthropic model picker infrastructure.
 *
 * One `getModel()` for the whole app was fine until we wanted to
 * demo "same curriculum on Opus vs Haiku" or pin the agentic Brain
 * chat to Opus while keeping Haiku for the 25×-week calendar loop.
 *
 * This module owns:
 *   • The catalogue of AI surfaces across apps/web (23 + 3 in
 *     packages/brain).
 *   • Their per-surface default models (curated tiers: Opus for
 *     agentic/long-form, Sonnet for balanced, Haiku for cheap/loop).
 *   • Resolution order — per-surface override → global override →
 *     per-surface default → DEFAULT_MODEL.
 *   • A small in-memory trace buffer so the override panel can show
 *     "last called" per row without adding network chatter.
 *
 * Callers pass `{ surface: '<id>' }` on any `callAI*` options and get
 * the right model automatically; the proxy already honours `body.model`
 * verbatim (see supabase/functions/anthropic-proxy).
 */

import { DEFAULT_MODEL } from './anthropicConfig';

// ============================================================================
// Catalogue — every AI surface in the app, ordered by group
// ============================================================================

export const AI_SURFACES = [
  // Brain (agentic + summarisation)
  'brain-chat',
  'brain-suggestions',
  'brain-summarize-conversation',
  'brain-summarize-correction',

  // Direct generators
  'philosophy-suggest',
  'curriculum-gen',
  'calendar-week',
  'calendar-all-weeks',
  'session-detail-drills',
  'session-planner',

  // Review
  'session-review-narrative',
  'review-suggest',
  'review-autofill',

  // Drill authoring
  'drill-voice',
  'drill-ai-complete',
  'drill-prompt-parser',
  'drill-organisation-ai',
  'drill-coaching-points-ai',
  'drill-brain-compose',

  // Coach progression
  'coach-eval',
  'coach-focus-guidance',

  // Voice overlays + onboarding
  'voice-onboarding',
  'voice-canvas',
  'voice-drill',
  'voice-problem',

  // Video
  'video-classify',
  'video-per-player',
  'video-chat',
  'ai-capture',
  'video-action-spotting',

  // Tactics
  'player-briefs',
] as const;
export type SurfaceId = typeof AI_SURFACES[number];

/** UI grouping for the override panel. Keys shown as accordion headers. */
export const SURFACE_GROUPS: Record<string, readonly SurfaceId[]> = {
  Brain:      ['brain-chat', 'brain-suggestions', 'brain-summarize-conversation', 'brain-summarize-correction'],
  Generators: ['philosophy-suggest', 'curriculum-gen', 'calendar-week', 'calendar-all-weeks', 'session-detail-drills', 'session-planner'],
  Review:     ['session-review-narrative', 'review-suggest', 'review-autofill'],
  Drills:     ['drill-voice', 'drill-ai-complete', 'drill-prompt-parser', 'drill-organisation-ai', 'drill-coaching-points-ai', 'drill-brain-compose'],
  Coach:      ['coach-eval', 'coach-focus-guidance'],
  Voice:      ['voice-onboarding', 'voice-canvas', 'voice-drill', 'voice-problem'],
  Video:      ['video-classify', 'video-per-player', 'video-chat', 'ai-capture', 'video-action-spotting'],
  Tactics:    ['player-briefs'],
};

export const SURFACE_LABEL: Record<SurfaceId, string> = {
  'brain-chat':                   'Brain chat (tool-use agent)',
  'brain-suggestions':            'Brain suggestion chips',
  'brain-summarize-conversation': 'Brain — conversation summary',
  'brain-summarize-correction':   'Brain — correction learning',
  'philosophy-suggest':           'Philosophy — AI suggest',
  'curriculum-gen':               'Curriculum generator',
  'calendar-week':                'Calendar — plan this week',
  'calendar-all-weeks':           'Calendar — plan all weeks',
  'session-detail-drills':        'Session — generate drills',
  'session-planner':              'Session planner panel',
  'session-review-narrative':     'Session review narrative',
  'review-suggest':               'Player review — column suggest',
  'review-autofill':              'Player review — auto-fill all',
  'drill-voice':                  'Drill — voice-to-drill',
  'drill-ai-complete':            'Drill — AI complete',
  'drill-prompt-parser':          'Drill Designer — prompt → DrillSpec',
  'drill-organisation-ai':        'Drill Designer — Organisation auto-draft',
  'drill-coaching-points-ai':     'Drill Designer — Coaching Points auto-draft',
  'drill-brain-compose':          'Drill Brain — AI-first compose (multi-keyframe)',
  'coach-eval':                   'Coach progression — weekly Brain evaluation',
  'coach-focus-guidance':         'Coach focus — discipline-imposing nudge before planner',
  'voice-onboarding':             'Team setup — voice onboarding',
  'voice-canvas':                 'Voice overlay — canvas diagram',
  'voice-drill':                  'Voice overlay — drill',
  'voice-problem':                'Voice overlay — problem-to-drill',
  'video-classify':               'Video — frame classification',
  'video-per-player':             'Video — per-player analysis',
  'video-chat':                   'Video — clip Q&A',
  'ai-capture':                   'Video import — AI capture',
  'video-action-spotting':        'Video — action spotting (auto-highlight)',
  'player-briefs':                'Tactics — player brief AI polish',
};

// ============================================================================
// Known models — surfaced in the dropdown
// ============================================================================

export interface ModelInfo {
  id: string;
  label: string;
  tier: 'opus' | 'sonnet' | 'haiku' | 'legacy';
  notes?: string;
}

export const KNOWN_MODELS: ModelInfo[] = [
  { id: 'claude-opus-4-7',           label: 'Opus 4.7',          tier: 'opus',   notes: 'Strongest reasoning, agentic. Premium pricing.' },
  { id: 'claude-sonnet-4-6',         label: 'Sonnet 4.6',        tier: 'sonnet', notes: 'Balanced. Good default.' },
  { id: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5',         tier: 'haiku',  notes: 'Fast + cheap. Vision capable.' },
  { id: 'claude-sonnet-4-20250514',  label: 'Sonnet 4 (legacy)', tier: 'legacy', notes: 'Pre-4.6 snapshot. Kept for back-compat.' },
];

export const TIER_COLOR: Record<ModelInfo['tier'], string> = {
  opus:   '#8E44AD',
  sonnet: '#2980B9',
  haiku:  '#27AE60',
  legacy: '#95A5A6',
};

/** Default model per surface. Opus for agentic/long-form, Sonnet for
 *  balanced, Haiku for cost-sensitive loops + quick extract. */
export const DEFAULT_MODEL_BY_SURFACE: Record<SurfaceId, string> = {
  // Opus — agentic / long-form / multi-column
  'brain-chat':                   'claude-opus-4-7',
  'curriculum-gen':               'claude-opus-4-7',
  'review-autofill':              'claude-opus-4-7',

  // Sonnet — balanced JSON draft jobs
  'philosophy-suggest':           'claude-sonnet-4-6',
  'review-suggest':               'claude-sonnet-4-6',
  'session-detail-drills':        'claude-sonnet-4-6',
  'session-review-narrative':     'claude-sonnet-4-6',
  'drill-ai-complete':            'claude-sonnet-4-6',
  'drill-prompt-parser':          'claude-sonnet-4-6',
  'drill-organisation-ai':        'claude-sonnet-4-6',
  'drill-coaching-points-ai':     'claude-sonnet-4-6',
  'drill-brain-compose':          'claude-sonnet-4-6',
  'coach-eval':                   'claude-sonnet-4-6',
  'coach-focus-guidance':         'claude-sonnet-4-6',
  'voice-onboarding':             'claude-opus-4-7',
  'video-classify':               'claude-sonnet-4-6',
  'video-per-player':             'claude-sonnet-4-6',
  'video-chat':                   'claude-sonnet-4-6',
  'ai-capture':                   'claude-sonnet-4-6',
  'video-action-spotting':        'claude-sonnet-4-6',
  'session-planner':              'claude-sonnet-4-6',
  'brain-summarize-conversation': 'claude-sonnet-4-6',

  // Haiku — cheap/fast extract + loop workloads
  'calendar-week':                'claude-haiku-4-5-20251001',
  'calendar-all-weeks':           'claude-haiku-4-5-20251001', // cost-sensitive loop
  'drill-voice':                  'claude-haiku-4-5-20251001',
  'voice-canvas':                 'claude-haiku-4-5-20251001',
  'voice-drill':                  'claude-haiku-4-5-20251001',
  'voice-problem':                'claude-haiku-4-5-20251001',
  'brain-summarize-correction':   'claude-haiku-4-5-20251001',
  'brain-suggestions':            'claude-haiku-4-5-20251001',
  'player-briefs':                'claude-haiku-4-5-20251001', // 11 short JSON polishes per regen — cost-sensitive
};

// ============================================================================
// Override storage
// ============================================================================

const MAP_KEY = '4cm_anthropic_model_map';
const GLOBAL_KEY = '4cm_anthropic_model'; // shared with legacy getModel()
const CHANGE_EVENT = '4cm_anthropic_model_change';
const TRACE_EVENT = '4cm_anthropic_trace';
const TRACE_MAX = 100;

function safeGet(key: string): string | null {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; }
  catch { return null; }
}
function safeSet(key: string, value: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch { /* ignore */ }
}
function fireChange(): void {
  try { window.dispatchEvent(new Event(CHANGE_EVENT)); } catch { /* ignore */ }
}

/** Read the per-surface override map. Returns `{}` on miss or parse error. */
export function getSurfaceMap(): Partial<Record<SurfaceId, string>> {
  const raw = safeGet(MAP_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Record<string, string>;
    if (parsed && typeof parsed === 'object') return parsed;
    return {};
  } catch {
    return {};
  }
}

/** Pin a surface to a specific model. Pass `null` to clear. */
export function setSurfaceModel(surface: SurfaceId, model: string | null): void {
  const map = getSurfaceMap();
  if (model === null || model === '') delete map[surface];
  else map[surface] = model;
  safeSet(MAP_KEY, Object.keys(map).length === 0 ? null : JSON.stringify(map));
  fireChange();
}

/** Wipe every per-surface override. Leaves the global override intact. */
export function clearAllSurfaceOverrides(): void {
  safeSet(MAP_KEY, null);
  fireChange();
}

/** Wipe BOTH the per-surface map and the global override. */
export function clearAllOverrides(): void {
  safeSet(MAP_KEY, null);
  safeSet(GLOBAL_KEY, null);
  fireChange();
}

/** JSON export of the full override state — handy for saving a test
 *  configuration and replaying later. */
export function exportOverrides(): string {
  return JSON.stringify({
    global: safeGet(GLOBAL_KEY),
    map: getSurfaceMap(),
  }, null, 2);
}

/** JSON import — returns true on success, false on parse error. */
export function importOverrides(json: string): boolean {
  try {
    const parsed = JSON.parse(json) as { global?: string | null; map?: Record<string, string> };
    if (parsed.global && typeof parsed.global === 'string') {
      safeSet(GLOBAL_KEY, parsed.global);
    } else if (parsed.global === null) {
      safeSet(GLOBAL_KEY, null);
    }
    if (parsed.map && typeof parsed.map === 'object') {
      safeSet(MAP_KEY, JSON.stringify(parsed.map));
    }
    fireChange();
    return true;
  } catch {
    return false;
  }
}

// ============================================================================
// Resolution — this is the one function every call site relies on
// ============================================================================

/** Resolve the effective model for a given surface. Order:
 *    1. Per-surface override (localStorage 4cm_anthropic_model_map)
 *    2. Global override      (localStorage 4cm_anthropic_model)
 *    3. Per-surface default  (DEFAULT_MODEL_BY_SURFACE)
 *    4. DEFAULT_MODEL        (from anthropicConfig) */
export function getModelFor(surface: SurfaceId): string {
  const map = getSurfaceMap();
  const pinned = map[surface];
  if (pinned && pinned.trim()) return pinned.trim();
  const global = safeGet(GLOBAL_KEY);
  if (global && global.trim()) return global.trim();
  return DEFAULT_MODEL_BY_SURFACE[surface] ?? DEFAULT_MODEL;
}

// ============================================================================
// Trace buffer — in-memory, cleared on reload, viewed from the panel
// ============================================================================

export interface TraceEntry { surface: SurfaceId; model: string; at: number; }

let traceBuf: TraceEntry[] = [];

/** Record a call-time (surface, model) pair. Called by ai.ts + visionEdge. */
export function recordCall(surface: SurfaceId, model: string): void {
  traceBuf.push({ surface, model, at: Date.now() });
  if (traceBuf.length > TRACE_MAX) traceBuf = traceBuf.slice(-TRACE_MAX);
  try { window.dispatchEvent(new Event(TRACE_EVENT)); } catch { /* ignore */ }
}

export function getTrace(): TraceEntry[] {
  return traceBuf.slice();
}

export function clearTrace(): void {
  traceBuf = [];
  try { window.dispatchEvent(new Event(TRACE_EVENT)); } catch { /* ignore */ }
}

/** Last trace entry for a surface, or null. */
export function lastCall(surface: SurfaceId): TraceEntry | null {
  for (let i = traceBuf.length - 1; i >= 0; i--) {
    if (traceBuf[i].surface === surface) return traceBuf[i];
  }
  return null;
}

// ============================================================================
// Events — useful for UI subscribers
// ============================================================================

export function onModelOverrideChange(cb: () => void): () => void {
  const handler = () => cb();
  try {
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener('storage', handler);
  } catch { /* ignore */ }
  return () => {
    try {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener('storage', handler);
    } catch { /* ignore */ }
  };
}

export function onTraceEvent(cb: () => void): () => void {
  const handler = () => cb();
  try { window.addEventListener(TRACE_EVENT, handler); } catch { /* ignore */ }
  return () => {
    try { window.removeEventListener(TRACE_EVENT, handler); } catch { /* ignore */ }
  };
}
